import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { getLocaleKey } from "@faire/retailer-visitor-shared/serialized-data/getLocaleKey";
import { getNavigationTreeHashCode } from "@faire/retailer-visitor-shared/serialized-data/getNavigationTreeHashCode";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { DEFAULT_LOCALE_KEY } from "@faire/web--source/common/consts/DEFAULT_LOCALE";
import { buildLocale } from "@faire/web--source/common/localization/utils";
import { logError } from "@faire/web--source/common/logging";
import { QueryParameters as NonPersonalizedCategoryNavigationQueryParams } from "@faire/web-api--source/api/v2/categories/navigation/get";
import { useQuery as useNonPersonalizedCategoryNavigationQuery, useInvalidateQuery as useInvalidateNonPersonalizedCategoryNavigationQuery } from "@faire/web-api--source/api/v2/categories/navigation/get-hooks";
import { QueryParameters as PersonalizedCategoryNavigationQueryParams } from "@faire/web-api--source/api/v2/categories/personalized/retailerToken/navigation/get";
import { useQuery as usePersonalizedCategoryNavigationQuery, useInvalidateQuery as useInvalidatePersonalizedCategoryNavigationQuery } from "@faire/web-api--source/api/v2/categories/personalized/retailerToken/navigation/get-hooks";
import { ClientPlatformType } from "@faire/web-api--source/indigofair/data/ClientPlatformType";
import { useCallback, useEffect, useMemo } from "react";
var SUPPORTED_PLATFORM_TYPES = [
    ClientPlatformType.CLIENT_MOBILE_WEB,
    ClientPlatformType.CLIENT_DESKTOP
];
export var useNavigationQuery = function(clientPlatformType) {
    var retailerToken = useRetailer().retailerToken;
    var navigationTreeHashCode = getNavigationTreeHashCode();
    var localeKey = getLocaleKey();
    var locale = buildLocale(localeKey !== null && localeKey !== void 0 ? localeKey : DEFAULT_LOCALE_KEY);
    var country = getSessionCountry();
    var shouldUsePersonalizedCategoryNavigationQuery = retailerToken !== undefined;
    var nonPersonalizedCategoryNavigationQuery = useMemo(function() {
        return NonPersonalizedCategoryNavigationQueryParams.build({
            clientPlatformType: clientPlatformType,
            hashCode: navigationTreeHashCode,
            locale: locale,
            country: country
        });
    }, [
        clientPlatformType,
        navigationTreeHashCode,
        locale,
        country
    ]);
    var personalizedCategoryNavigationQuery = useMemo(function() {
        return PersonalizedCategoryNavigationQueryParams.build({
            clientPlatformType: clientPlatformType,
            hashCode: navigationTreeHashCode,
            locale: locale
        });
    }, [
        clientPlatformType,
        navigationTreeHashCode,
        locale
    ]);
    var _useNonPersonalizedCategoryNavigationQuery = useNonPersonalizedCategoryNavigationQuery(nonPersonalizedCategoryNavigationQuery, {
        enabled: !shouldUsePersonalizedCategoryNavigationQuery,
        staleTime: Infinity
    }), nonPersonalizedCategoryNavigationData = _useNonPersonalizedCategoryNavigationQuery.data, isNonPersonalizedCategoryNavigationError = _useNonPersonalizedCategoryNavigationQuery.isError, nonPersonalizedCategoryNavigationError = _useNonPersonalizedCategoryNavigationQuery.error;
    var _usePersonalizedCategoryNavigationQuery = usePersonalizedCategoryNavigationQuery(retailerToken !== null && retailerToken !== void 0 ? retailerToken : "", personalizedCategoryNavigationQuery, {
        enabled: shouldUsePersonalizedCategoryNavigationQuery,
        staleTime: Infinity
    }), personalizedCategoryNavigationData = _usePersonalizedCategoryNavigationQuery.data, isPersonalizedCategoryNavigationError = _usePersonalizedCategoryNavigationQuery.isError, personalizedCategoryNavigationError = _usePersonalizedCategoryNavigationQuery.error;
    useEffect(function() {
        if (isNonPersonalizedCategoryNavigationError) {
            logError(nonPersonalizedCategoryNavigationError);
        }
    }, [
        isNonPersonalizedCategoryNavigationError,
        nonPersonalizedCategoryNavigationError
    ]);
    useEffect(function() {
        if (isPersonalizedCategoryNavigationError) {
            logError(personalizedCategoryNavigationError);
        }
    }, [
        isPersonalizedCategoryNavigationError,
        personalizedCategoryNavigationError
    ]);
    var invalidateNonPersonalizedCategoryNavigationQuery = useInvalidateNonPersonalizedCategoryNavigationQuery();
    var invalidatePersonalizedCategoryNavigationQuery = useInvalidatePersonalizedCategoryNavigationQuery();
    var invalidateNavigationQueries = useCallback(/*#__PURE__*/ _async_to_generator(function() {
        var _iteratorNormalCompletion, _didIteratorError, _iteratorError, _iterator, _step, supportedPlatformType;
        return _ts_generator(this, function(_state) {
            _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
            try {
                for(_iterator = SUPPORTED_PLATFORM_TYPES[Symbol.iterator](); !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                    supportedPlatformType = _step.value;
                    invalidateNonPersonalizedCategoryNavigationQuery(_object_spread_props(_object_spread({}, nonPersonalizedCategoryNavigationQuery), {
                        clientPlatformType: supportedPlatformType
                    }));
                    invalidatePersonalizedCategoryNavigationQuery(retailerToken !== null && retailerToken !== void 0 ? retailerToken : "", _object_spread_props(_object_spread({}, personalizedCategoryNavigationQuery), {
                        clientPlatformType: supportedPlatformType
                    }));
                }
            } catch (err) {
                _didIteratorError = true;
                _iteratorError = err;
            } finally{
                try {
                    if (!_iteratorNormalCompletion && _iterator.return != null) {
                        _iterator.return();
                    }
                } finally{
                    if (_didIteratorError) {
                        throw _iteratorError;
                    }
                }
            }
            return [
                2
            ];
        });
    }), [
        retailerToken,
        invalidateNonPersonalizedCategoryNavigationQuery,
        nonPersonalizedCategoryNavigationQuery,
        invalidatePersonalizedCategoryNavigationQuery,
        personalizedCategoryNavigationQuery
    ]);
    var navigationLinkGroup = useMemo(function() {
        var navigationLinkData = shouldUsePersonalizedCategoryNavigationQuery ? personalizedCategoryNavigationData : nonPersonalizedCategoryNavigationData;
        var _navigationLinkData_main_navigation_link_group;
        return (_navigationLinkData_main_navigation_link_group = navigationLinkData === null || navigationLinkData === void 0 ? void 0 : navigationLinkData.main_navigation_link_group) !== null && _navigationLinkData_main_navigation_link_group !== void 0 ? _navigationLinkData_main_navigation_link_group : [];
    }, [
        nonPersonalizedCategoryNavigationData,
        personalizedCategoryNavigationData,
        shouldUsePersonalizedCategoryNavigationQuery
    ]);
    return {
        navigationLinkGroup: navigationLinkGroup,
        invalidateNavigationQueries: invalidateNavigationQueries
    };
};
